import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import {
  FaYoutube,
  FaSearch,
  FaTimes,
  FaSyncAlt,
  FaPlay,
  FaEye,
  FaMagic,
  FaClock,
  FaTrash,
  FaCopy,
  FaExternalLinkAlt,
  FaUser,
  FaFileCsv,
  FaInbox,
  FaExclamationTriangle,
  FaTrophy,
  FaFilm,
  FaMobileAlt,
  FaLayerGroup,
  FaChevronLeft,
  FaChevronRight,
  FaUserSlash,
  FaFilter,
  FaCrown,
} from "react-icons/fa";
import { useBaseUrl } from "../Contexts/BaseUrlContext";
import { useStateContext } from "../Contexts/ContextProvider";

/* ================= styles ================= */

const CSS = `
@keyframes ytFadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
@keyframes ytFade { from { opacity: 0; } to { opacity: 1; } }
@keyframes ytZoom { from { opacity: 0; transform: scale(.95); } to { opacity: 1; transform: none; } }
@keyframes ytBar { from { width: 0; } }
@keyframes ytGlow { 0%,100% { opacity: .55; } 50% { opacity: .9; } }
.yt-in { animation: ytFadeUp .5s cubic-bezier(.2,.7,.2,1) both; }
.yt-fade { animation: ytFade .2s ease both; }
.yt-zoom { animation: ytZoom .25s cubic-bezier(.2,.8,.2,1) both; }
.yt-bar { animation: ytBar .9s cubic-bezier(.2,.7,.2,1) both; }
.yt-glow { animation: ytGlow 4s ease-in-out infinite; }
.yt-press { transition: transform .15s ease, background-color .2s ease, border-color .2s ease, color .2s ease, box-shadow .2s ease; }
.yt-press:hover { transform: translateY(-1px); }
.yt-press:active { transform: scale(.96); }
.yt-card { transition: transform .25s cubic-bezier(.2,.8,.2,1), box-shadow .25s ease; }
.yt-card:hover { transform: translateY(-4px); box-shadow: 0 20px 36px -18px rgba(15,23,42,.45); }
.yt-card .yt-thumb img { transition: transform .5s cubic-bezier(.2,.8,.2,1); }
.yt-card:hover .yt-thumb img { transform: scale(1.06); }
.yt-card .yt-hover { opacity: 0; transition: opacity .2s ease; }
.yt-card:hover .yt-hover, .yt-card:focus-within .yt-hover { opacity: 1; }
.yt-card .yt-playbtn { transform: scale(.8); opacity: 0; transition: transform .25s cubic-bezier(.2,.8,.2,1), opacity .2s ease; }
.yt-card:hover .yt-playbtn { transform: scale(1); opacity: 1; }
.yt-mark { background: #fde68a; color: inherit; border-radius: 3px; padding: 0 1px; }
@media (prefers-reduced-motion: reduce) {
  .yt-in, .yt-fade, .yt-zoom, .yt-bar, .yt-glow { animation: none; }
  .yt-press:hover, .yt-press:active, .yt-card:hover, .yt-card:hover .yt-thumb img { transform: none; }
  .yt-card .yt-playbtn { transform: none; }
}
`;

const CARD =
  "bg-white dark:bg-secondary-dark-bg rounded-2xl border border-slate-200 dark:border-slate-700";
const INPUT =
  "rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500";

/* ================= helpers ================= */

const DAY = 24 * 60 * 60 * 1000;
const fmt = (n) => Number(n || 0).toLocaleString("en-IN");
const compact = (n) => {
  const v = Number(n || 0);
  if (v >= 10000000) return `${(v / 10000000).toFixed(1)}Cr`;
  if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return String(v);
};

const parseAny = (s) => {
  if (!s) return null;
  const d = new Date(String(s).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? null : d;
};
const fmtDate = (d) =>
  d ? d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-";
const fmtTime = (d) =>
  d ? d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "";
const ago = (d) => {
  if (!d) return "";
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hr ago`;
  const days = Math.floor(s / 86400);
  if (days < 30) return `${days} day${days > 1 ? "s" : ""} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months > 1 ? "s" : ""} ago`;
  return `${Math.floor(months / 12)} yr ago`;
};
const fmtWatch = (s) => {
  const n = Number(s || 0);
  if (!n) return "0:00";
  return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, "0")}`;
};

const ytId = (url) => {
  const m = String(url || "").match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
};
const looksShort = (url) => /youtube\.com\/shorts\//i.test(String(url || ""));
const isShort = (v) => v.link_type === "Youtube Short";
const mismatch = (v) => (looksShort(v.link_url) && !isShort(v) ? "short-as-video" : null);
const deletedUser = (v) => !v.user_name || v.user_name === "User Deleted";

const Hi = ({ text, q }) => {
  const s = String(text ?? "");
  if (!q || !s) return <>{s}</>;
  const i = s.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return <>{s}</>;
  return (
    <>
      {s.slice(0, i)}
      <mark className="yt-mark">{s.slice(i, i + q.length)}</mark>
      {s.slice(i + q.length)}
    </>
  );
};

const Avatar = ({ src, name, size = 32 }) => {
  const [err, setErr] = useState(false);
  const initials =
    String(name || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?";
  if (src && !err)
    return <img src={src} alt="" onError={() => setErr(true)} className="rounded-full object-cover shrink-0 bg-slate-100" style={{ width: size, height: size }} />;
  return (
    <span className="rounded-full shrink-0 flex items-center justify-center bg-slate-200 text-slate-600 font-semibold dark:bg-slate-600 dark:text-slate-200" style={{ width: size, height: size, fontSize: size * 0.38 }}>
      {initials}
    </span>
  );
};

/* ================= page ================= */

const YouTube = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);

  const [tab, setTab] = useState("all"); // all | video | short
  const [query, setQuery] = useState("");
  const [creator, setCreator] = useState(null); // user_id
  const [flag, setFlag] = useState("all"); // all | mismatch | deleted | zero
  const [range, setRange] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [checked, setChecked] = useState([]);
  const [page, setPage] = useState(1);
  const PAGE = 16;

  const [player, setPlayer] = useState(null); // index in filtered
  const [confirm, setConfirm] = useState(null);
  const [working, setWorking] = useState(false);

  const toast = useRef(null);
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const { baseUrl } = useBaseUrl();
  const { currentMode } = useStateContext();

  /* ----- API (same actions as before) ----- */

  const post = useCallback(
    async (action, extra = {}) => {
      const fd = new FormData();
      fd.append("action", action);
      Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
      const res = await fetch(`${baseUrl}/videos.php`, { method: "POST", body: fd, credentials: "include" });
      return res.json();
    },
    [baseUrl]
  );

  const fetchVideos = useCallback(async () => {
    setLoading(true);
    try {
      const [v, s] = await Promise.all([post("videos-list", { type: "Youtube Video" }), post("videos-list", { type: "Youtube Short" })]);
      setVideos([...(Array.isArray(v) ? v : []), ...(Array.isArray(s) ? s : [])]);
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to fetch YouTube videos" });
    }
    setLoading(false);
  }, [post]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  const runDelete = async () => {
    if (!confirm?.length) return;
    setWorking(true);
    let ok = 0;
    for (const v of confirm) {
      try {
        const res = await post("delete-video", { post_id: v.post_id });
        if (res && res.status === false) throw new Error("Failed");
        ok += 1;
      } catch (e) {
        console.error(e);
      }
    }
    setWorking(false);
    setConfirm(null);
    setChecked([]);
    setPlayer(null);
    if (ok) toast.current?.show({ severity: "success", summary: "Deleted", detail: ok === 1 ? "Video deleted successfully" : `${ok} videos deleted` });
    if (ok < confirm.length) toast.current?.show({ severity: "error", summary: "Error", detail: "Some videos could not be deleted" });
    fetchVideos();
  };

  /* ----- derived ----- */

  const q = query.trim();

  const inTab = useMemo(() => videos.filter((v) => (tab === "all" ? true : tab === "short" ? isShort(v) : !isShort(v))), [videos, tab]);

  const creators = useMemo(() => {
    const m = {};
    inTab.forEach((v) => {
      const k = v.user_id;
      if (!m[k]) m[k] = { user_id: k, name: v.user_name, profile: v.user_profile, count: 0, views: 0, karma: 0 };
      m[k].count += 1;
      m[k].views += Number(v.views_num || 0);
      m[k].karma += Number(v.point_used || 0);
    });
    return Object.values(m).sort((a, b) => b.views - a.views || b.count - a.count);
  }, [inTab]);

  const stats = useMemo(() => {
    const views = inTab.reduce((s, v) => s + Number(v.views_num || 0), 0);
    const karma = inTab.reduce((s, v) => s + Number(v.point_used || 0), 0);
    return {
      total: inTab.length,
      views,
      karma,
      creators: creators.length,
      mismatch: inTab.filter(mismatch).length,
      deleted: inTab.filter(deletedUser).length,
      zero: inTab.filter((v) => !Number(v.views_num)).length,
    };
  }, [inTab, creators]);

  const counts = useMemo(
    () => ({ all: videos.length, video: videos.filter((v) => !isShort(v)).length, short: videos.filter(isShort).length }),
    [videos]
  );

  const maxViews = useMemo(() => Math.max(1, ...inTab.map((v) => Number(v.views_num || 0))), [inTab]);

  const filtered = useMemo(() => {
    const ql = q.toLowerCase();
    const now = Date.now();
    let l = inTab.filter((v) => {
      if (creator && String(v.user_id) !== String(creator)) return false;
      if (flag === "mismatch" && !mismatch(v)) return false;
      if (flag === "deleted" && !deletedUser(v)) return false;
      if (flag === "zero" && Number(v.views_num)) return false;
      if (range !== "all") {
        const d = parseAny(v.link_date);
        if (!d) return false;
        if (range === "7d" && now - d.getTime() > 7 * DAY) return false;
        if (range === "30d" && now - d.getTime() > 30 * DAY) return false;
      }
      if (!ql) return true;
      return [v.link_url, ytId(v.link_url), v.post_id, v.user_name, v.user_id].map((x) => String(x ?? "").toLowerCase()).some((x) => x.includes(ql));
    });
    l = [...l].sort((a, b) => {
      if (sortBy === "views") return Number(b.views_num || 0) - Number(a.views_num || 0);
      if (sortBy === "least") return Number(a.views_num || 0) - Number(b.views_num || 0);
      if (sortBy === "karma") return Number(b.point_used || 0) - Number(a.point_used || 0);
      const da = parseAny(a.link_date)?.getTime() || 0;
      const db = parseAny(b.link_date)?.getTime() || 0;
      return sortBy === "oldest" ? da - db : db - da;
    });
    return l;
  }, [inTab, q, creator, flag, range, sortBy]);

  useEffect(() => setPage(1), [q, creator, flag, range, sortBy, tab]);
  useEffect(() => {
    const valid = new Set(videos.map((v) => v.post_id));
    setChecked((c) => c.filter((id) => valid.has(id)));
  }, [videos]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice((safePage - 1) * PAGE, safePage * PAGE);
  const allPageChecked = pageRows.length > 0 && pageRows.every((v) => checked.includes(v.post_id));
  const checkedVideos = videos.filter((v) => checked.includes(v.post_id));
  const creatorInfo = creators.find((c) => String(c.user_id) === String(creator));

  const hasFilters = q || creator || flag !== "all" || range !== "all";
  const clearFilters = () => {
    setQuery("");
    setCreator(null);
    setFlag("all");
    setRange("all");
  };

  const toggleCheck = (id) => setChecked((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));

  const copyText = (text, label = "Copied") => {
    navigator.clipboard?.writeText(String(text));
    toast.current?.show({ severity: "info", summary: label, detail: String(text), life: 1500 });
  };

  const exportCsv = (list) => {
    const head = ["Video Id", "Type", "Upload By", "User Id", "Video Link", "Watch Time", "Karma Used", "Views", "Date Time"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = list.map((v) => [v.post_id, v.link_type, v.user_name, v.user_id, v.link_url, v.watch_time, v.point_used, v.views_num, v.link_date].map(esc).join(","));
    const csv = "\uFEFF" + [head.map(esc).join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ycnp-youtube-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    const onKey = (e) => {
      const typing = ["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName);
      if (confirm) {
        if (e.key === "Escape" && !working) setConfirm(null);
        return;
      }
      if (player !== null) {
        if (e.key === "Escape") setPlayer(null);
        if (e.key === "ArrowRight") setPlayer((i) => Math.min(filtered.length - 1, i + 1));
        if (e.key === "ArrowLeft") setPlayer((i) => Math.max(0, i - 1));
        return;
      }
      if (!typing && e.key === "/") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [confirm, working, player, filtered.length]);

  const current = player !== null ? filtered[player] : null;

  /* ----- pieces ----- */

  const Thumb = ({ v, big }) => {
    const id = ytId(v.link_url);
    const short = isShort(v) || looksShort(v.link_url);
    return (
      <div className="yt-thumb relative overflow-hidden bg-slate-900" style={{ aspectRatio: "16/9" }}>
        {id ? (
          <>
            {short && <img src={`https://img.youtube.com/vi/${id}/hqdefault.jpg`} alt="" className="absolute inset-0 w-full h-full object-cover blur-md scale-110 opacity-60" loading="lazy" />}
            <img
              src={`https://img.youtube.com/vi/${id}/${big ? "maxresdefault" : "hqdefault"}.jpg`}
              onError={(e) => {
                if (!e.target.dataset.fb) {
                  e.target.dataset.fb = "1";
                  e.target.src = `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
                }
              }}
              alt=""
              loading="lazy"
              className={short ? "absolute inset-y-0 left-1/2 -translate-x-1/2 h-full object-cover" : "absolute inset-0 w-full h-full object-cover"}
              style={short ? { aspectRatio: "9/16" } : {}}
            />
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white/70 text-xs gap-1">
            <FaYoutube size={30} />
            Link not recognised
          </div>
        )}
      </div>
    );
  };

  const statCards = [
    { label: tab === "short" ? "Shorts" : tab === "video" ? "Videos" : "Total uploads", value: stats.total, icon: FaLayerGroup, tint: "from-red-500 to-rose-600" },
    { label: "Total views", value: stats.views, icon: FaEye, tint: "from-cyan-500 to-sky-600" },
    { label: "Karma spent", value: stats.karma, icon: FaMagic, tint: "from-violet-500 to-fuchsia-600" },
    { label: "Creators", value: stats.creators, icon: FaUser, tint: "from-emerald-500 to-teal-600" },
  ];

  const flags = [
    { id: "all", label: "Everything" },
    { id: "mismatch", label: `Shorts saved as Video (${stats.mismatch})`, warn: stats.mismatch > 0 },
    { id: "deleted", label: `Uploader deleted (${stats.deleted})`, warn: stats.deleted > 0 },
    { id: "zero", label: `No views yet (${stats.zero})` },
  ];

  /* ================= render ================= */

  return (
    <div style={currentMode === "Dark" ? { backgroundColor: "#20232A" } : {}} className="w-full p-4 sm:p-6 lg:p-8 bg-slate-50 min-h-screen">
      <style>{CSS}</style>
      <Toast ref={toast} />

      {/* Hero */}
      <div className="yt-in relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-900 to-red-950 text-white p-6 sm:p-8">
        <div className="yt-glow pointer-events-none absolute -top-24 -right-16 w-80 h-80 rounded-full bg-red-600/40 blur-3xl" />
        <div className="yt-glow pointer-events-none absolute -bottom-28 left-1/3 w-72 h-72 rounded-full bg-rose-500/20 blur-3xl" style={{ animationDelay: "2s" }} />
        <div className="relative flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-sm text-white/60">Videos</p>
            <h1 className="mt-1 flex items-center gap-3 text-3xl font-bold tracking-tight">
              <span className="w-11 h-11 rounded-xl bg-red-600 flex items-center justify-center shadow-lg shadow-red-900/50">
                <FaYoutube size={22} />
              </span>
              YouTube Videos
            </h1>
            <p className="mt-2 text-sm text-white/70 max-w-md">Every YouTube video and Short promoted by creators. Watch, check and remove from one place.</p>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => exportCsv(filtered)} disabled={!filtered.length} className="yt-press inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-white/10 hover:bg-white/20 text-sm font-medium disabled:opacity-40">
              <FaFileCsv /> Export
            </button>
            <button type="button" onClick={fetchVideos} disabled={loading} className="yt-press inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-red-600 hover:bg-red-500 text-sm font-semibold shadow-lg shadow-red-900/40 disabled:opacity-60">
              <FaSyncAlt size={12} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="relative mt-6 inline-flex p-1 rounded-2xl bg-white/10 backdrop-blur">
          {[
            { id: "all", label: "All", icon: FaLayerGroup },
            { id: "video", label: "Videos", icon: FaFilm },
            { id: "short", label: "Shorts", icon: FaMobileAlt },
          ].map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => {
                  setTab(t.id);
                  setCreator(null);
                }}
                className={`inline-flex items-center gap-2 h-10 px-4 rounded-xl text-sm font-semibold transition-all active:scale-95 ${active ? "bg-white text-slate-900 shadow" : "text-white/80 hover:text-white"}`}
              >
                <Icon size={12} /> {t.label}
                <span className={`min-w-[26px] h-6 px-1.5 rounded-lg text-xs flex items-center justify-center tabular-nums ${active ? "bg-red-600 text-white" : "bg-white/15"}`}>{counts[t.id]}</span>
              </button>
            );
          })}
        </div>

        {/* Stats */}
        <div className="relative mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3">
          {statCards.map((c, i) => {
            const Icon = c.icon;
            return (
              <div key={c.label} className="yt-in rounded-2xl bg-white/[.06] border border-white/10 p-4" style={{ animationDelay: `${100 + i * 70}ms` }}>
                <span className={`w-9 h-9 rounded-lg bg-gradient-to-br ${c.tint} flex items-center justify-center shadow`}>
                  <Icon size={14} />
                </span>
                <p className="mt-3 text-2xl font-bold tabular-nums">{loading && !videos.length ? "–" : fmt(c.value)}</p>
                <p className="text-xs text-white/60">{c.label}</p>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-4 mt-4">
        {/* Main */}
        <div className="xl:col-span-3 min-w-0">
          {/* Search + filters */}
          <div className={`${CARD} yt-in p-4`} style={{ animationDelay: "200ms" }}>
            <div className="flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input ref={searchRef} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by link, video ID, post ID, creator name or user ID" className={`${INPUT} w-full h-12 pl-11 pr-12 text-base`} />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {query ? (
                    <button type="button" onClick={() => setQuery("")} className="p-1.5 text-slate-400 hover:text-slate-700" aria-label="Clear search">
                      <FaTimes size={13} />
                    </button>
                  ) : (
                    <kbd className="hidden sm:inline text-xs text-slate-400 border border-slate-200 dark:border-slate-600 rounded px-1.5 py-0.5">/</kbd>
                  )}
                </div>
              </div>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={`${INPUT} h-12 px-3 md:w-44`}>
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="views">Most views</option>
                <option value="least">Least views</option>
                <option value="karma">Most karma</option>
              </select>
              <select value={range} onChange={(e) => setRange(e.target.value)} className={`${INPUT} h-12 px-3 md:w-40`}>
                <option value="all">Any date</option>
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
              </select>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <FaFilter className="text-slate-400" size={11} />
              {flags.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setFlag(f.id)}
                  className={`yt-press h-8 px-3 rounded-full text-xs font-semibold border ${
                    flag === f.id
                      ? "bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900"
                      : f.warn
                      ? "bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-900/20 dark:text-amber-200 dark:border-amber-800"
                      : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600"
                  }`}
                >
                  {f.label}
                </button>
              ))}
              {creatorInfo && (
                <span className="inline-flex items-center gap-2 h-8 pl-1 pr-2 rounded-full bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-200 text-xs font-semibold">
                  <Avatar src={creatorInfo.profile} name={creatorInfo.name} size={24} />
                  {creatorInfo.name}
                  <button type="button" onClick={() => setCreator(null)} aria-label="Remove creator filter">
                    <FaTimes size={10} />
                  </button>
                </span>
              )}
              {hasFilters && (
                <button type="button" onClick={clearFilters} className="ml-auto text-sm font-medium text-red-600 hover:underline">
                  Clear all
                </button>
              )}
            </div>
          </div>

          {/* Result bar */}
          <div className="flex flex-wrap items-center gap-3 mt-4 mb-3">
            <label className="inline-flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 cursor-pointer">
              <input
                type="checkbox"
                checked={allPageChecked}
                onChange={() => setChecked((c) => (allPageChecked ? c.filter((id) => !pageRows.some((v) => v.post_id === id)) : Array.from(new Set([...c, ...pageRows.map((v) => v.post_id)]))))}
                className="w-4 h-4 accent-red-600"
              />
              Select page
            </label>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {q ? (
                <>
                  <b>{filtered.length}</b> result{filtered.length === 1 ? "" : "s"} for "<b>{q}</b>"
                </>
              ) : (
                <>
                  <b>{filtered.length}</b> {tab === "short" ? "Shorts" : tab === "video" ? "videos" : "uploads"}
                </>
              )}
            </p>
            {checked.length > 0 && (
              <div className="yt-fade ml-auto flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{checked.length} selected</span>
                <button type="button" onClick={() => setConfirm(checkedVideos)} className="yt-press inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold">
                  <FaTrash size={11} /> Delete
                </button>
                <button type="button" onClick={() => exportCsv(checkedVideos)} className="yt-press inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200">
                  <FaFileCsv size={11} /> Export
                </button>
                <button type="button" onClick={() => setChecked([])} className="text-sm text-slate-500 hover:underline">
                  Clear
                </button>
              </div>
            )}
          </div>

          {/* Grid */}
          {loading && !videos.length ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className={`${CARD} overflow-hidden`}>
                  <div className="bg-slate-200 dark:bg-slate-700 animate-pulse" style={{ aspectRatio: "16/9" }} />
                  <div className="p-3 flex gap-3">
                    <div className="w-9 h-9 rounded-full bg-slate-200 dark:bg-slate-700 animate-pulse" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 rounded bg-slate-200 dark:bg-slate-700 animate-pulse" />
                      <div className="h-3 w-2/3 rounded bg-slate-200 dark:bg-slate-700 animate-pulse" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : pageRows.length === 0 ? (
            <div className={`${CARD} px-5 py-16 text-center`}>
              <FaInbox className="mx-auto text-slate-300" size={36} />
              <p className="mt-3 font-semibold text-slate-700 dark:text-slate-200">{q ? `No video found for "${q}"` : "No videos here"}</p>
              {hasFilters && (
                <button type="button" onClick={clearFilters} className="mt-2 text-sm font-medium text-red-600 hover:underline">
                  Clear all filters
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
              {pageRows.map((v, i) => {
                const d = parseAny(v.link_date);
                const sel = checked.includes(v.post_id);
                const mm = mismatch(v);
                const views = Number(v.views_num || 0);
                const idx = (safePage - 1) * PAGE + i;
                return (
                  <article
                    key={v.post_id}
                    className={`yt-card yt-in rounded-2xl bg-white dark:bg-secondary-dark-bg border overflow-hidden ${sel ? "border-red-500 ring-2 ring-red-500/30" : "border-slate-200 dark:border-slate-700"}`}
                    style={{ animationDelay: `${Math.min(i, 12) * 35}ms` }}
                  >
                    <div className="relative">
                      <button type="button" onClick={() => setPlayer(idx)} className="block w-full text-left" aria-label="Play video">
                        {Thumb({ v })}
                        <span className="yt-playbtn absolute inset-0 m-auto w-14 h-14 rounded-full bg-red-600 text-white flex items-center justify-center shadow-2xl shadow-red-900/50">
                          <FaPlay className="ml-1" />
                        </span>
                      </button>
                      <label className={`absolute top-2 left-2 w-7 h-7 rounded-lg bg-white/95 dark:bg-slate-900/80 flex items-center justify-center cursor-pointer shadow ${sel ? "" : "yt-hover"}`}>
                        <input type="checkbox" checked={sel} onChange={() => toggleCheck(v.post_id)} className="w-4 h-4 accent-red-600" aria-label={`Select ${v.post_id}`} />
                      </label>
                      <div className="absolute top-2 right-2 flex flex-col items-end gap-1">
                        <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-md text-white ${isShort(v) ? "bg-rose-500" : "bg-red-600"}`}>
                          {isShort(v) ? <FaMobileAlt size={9} /> : <FaFilm size={9} />} {isShort(v) ? "SHORT" : "VIDEO"}
                        </span>
                        {mm && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-amber-400 text-amber-950" title="This link is a Short but was saved as a Video">
                            <FaExclamationTriangle size={8} /> Short link
                          </span>
                        )}
                      </div>
                      <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 text-[11px] font-semibold px-1.5 py-0.5 rounded bg-black/80 text-white tabular-nums">
                        <FaClock size={9} /> {fmtWatch(v.watch_time)}
                      </span>
                      {/* views progress along the bottom edge */}
                      <div className="absolute bottom-0 inset-x-0 h-1 bg-black/30">
                        <div className="yt-bar h-full bg-red-600" style={{ width: `${(views / maxViews) * 100}%` }} />
                      </div>
                    </div>

                    <div className="p-3 flex gap-3">
                      <button type="button" onClick={() => setCreator(v.user_id)} title="Show only this creator" className="yt-press shrink-0">
                        <Avatar src={v.user_profile} name={v.user_name} size={36} />
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-900 dark:text-white truncate" title={v.link_url}>
                          <Hi text={ytId(v.link_url) || v.link_url} q={q} />
                        </p>
                        <button type="button" onClick={() => setCreator(v.user_id)} className={`block text-sm truncate hover:underline ${deletedUser(v) ? "text-rose-500 italic" : "text-slate-600 dark:text-slate-300"}`}>
                          <Hi text={v.user_name || "User Deleted"} q={q} />
                        </button>
                        <p className="text-xs text-slate-500">
                          {compact(views)} views <span className="mx-1">•</span> {ago(d)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 px-3 pb-3">
                      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-lg bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
                        <FaMagic size={9} /> {fmt(v.point_used)} karma
                      </span>
                      <div className="ml-auto flex gap-1">
                        <button type="button" onClick={() => copyText(v.link_url, "Link copied")} className="yt-press w-8 h-8 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center justify-center" title="Copy link" aria-label="Copy link">
                          <FaCopy size={11} />
                        </button>
                        <a href={v.link_url} target="_blank" rel="noreferrer" className="yt-press w-8 h-8 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center justify-center" title="Open on YouTube" aria-label="Open on YouTube">
                          <FaExternalLinkAlt size={11} />
                        </a>
                        <button type="button" onClick={() => setConfirm([v])} className="yt-press w-8 h-8 rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center justify-center" title="Delete video" aria-label="Delete video">
                          <FaTrash size={11} />
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {filtered.length > PAGE && (
            <div className="flex items-center justify-center gap-3 mt-6 text-sm">
              <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1} className="yt-press w-10 h-10 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg flex items-center justify-center disabled:opacity-40" aria-label="Previous page">
                <FaChevronLeft size={11} />
              </button>
              <span className="tabular-nums text-slate-600 dark:text-slate-300">
                Page <b>{safePage}</b> of {totalPages}
              </span>
              <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages} className="yt-press w-10 h-10 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg flex items-center justify-center disabled:opacity-40" aria-label="Next page">
                <FaChevronRight size={11} />
              </button>
            </div>
          )}
        </div>

        {/* Creator leaderboard */}
        <aside className={`${CARD} yt-in p-4 xl:sticky xl:top-4 self-start`} style={{ animationDelay: "260ms" }}>
          <div className="flex items-center gap-2">
            <FaTrophy className="text-amber-500" />
            <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Top creators</h2>
          </div>
          <p className="text-xs text-slate-500">By total views. Click to see only their videos.</p>
          <ul className="mt-3 space-y-1 max-h-[60vh] overflow-y-auto">
            {creators.slice(0, 15).map((c, i) => {
              const active = String(creator) === String(c.user_id);
              return (
                <li key={c.user_id}>
                  <button
                    type="button"
                    onClick={() => setCreator(active ? null : c.user_id)}
                    className={`yt-press w-full flex items-center gap-3 rounded-xl px-2 py-2 text-left ${active ? "bg-red-50 dark:bg-red-900/20 ring-1 ring-red-300" : "hover:bg-slate-50 dark:hover:bg-slate-700/40"}`}
                  >
                    <span className={`w-6 text-center text-xs font-bold ${i === 0 ? "text-amber-500" : i < 3 ? "text-slate-500" : "text-slate-400"}`}>{i === 0 ? <FaCrown className="mx-auto" /> : i + 1}</span>
                    <Avatar src={c.profile} name={c.name} size={32} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${!c.name || c.name === "User Deleted" ? "text-rose-500 italic" : "text-slate-900 dark:text-white"}`}>{c.name || "User Deleted"}</p>
                      <p className="text-[11px] text-slate-500">
                        {c.count} upload{c.count > 1 ? "s" : ""} • {compact(c.views)} views
                      </p>
                    </div>
                  </button>
                </li>
              );
            })}
            {!creators.length && <li className="text-sm text-slate-500 py-4 text-center">No creators yet</li>}
          </ul>
          {(stats.mismatch > 0 || stats.deleted > 0) && (
            <div className="mt-4 space-y-2">
              {stats.mismatch > 0 && (
                <button type="button" onClick={() => setFlag("mismatch")} className="yt-press w-full flex items-start gap-2 rounded-xl bg-amber-50 dark:bg-amber-900/20 p-3 text-left text-xs text-amber-900 dark:text-amber-200">
                  <FaExclamationTriangle className="mt-0.5 shrink-0" />
                  <span>
                    <b>{stats.mismatch}</b> Short link{stats.mismatch > 1 ? "s were" : " was"} saved as a normal Video. Tap to review.
                  </span>
                </button>
              )}
              {stats.deleted > 0 && (
                <button type="button" onClick={() => setFlag("deleted")} className="yt-press w-full flex items-start gap-2 rounded-xl bg-rose-50 dark:bg-rose-900/20 p-3 text-left text-xs text-rose-900 dark:text-rose-200">
                  <FaUserSlash className="mt-0.5 shrink-0" />
                  <span>
                    <b>{stats.deleted}</b> video{stats.deleted > 1 ? "s belong" : " belongs"} to deleted users. Tap to review.
                  </span>
                </button>
              )}
            </div>
          )}
        </aside>
      </div>

      {/* ================= Player ================= */}
      {current && (
        <div className="fixed inset-0 flex items-center justify-center p-3 sm:p-6" style={{ zIndex: 1100 }}>
          <div className="yt-fade absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={() => setPlayer(null)} />
          <div className="yt-zoom relative w-full max-w-5xl grid grid-cols-1 lg:grid-cols-3 gap-0 rounded-2xl overflow-hidden bg-slate-900 text-white shadow-2xl max-h-[92vh]">
            <div className="lg:col-span-2 bg-black flex items-center justify-center">
              {ytId(current.link_url) ? (
                <div className="w-full" style={{ aspectRatio: isShort(current) || looksShort(current.link_url) ? "9/16" : "16/9", maxHeight: "92vh" }}>
                  <iframe
                    key={current.post_id}
                    title="YouTube video player"
                    src={`https://www.youtube-nocookie.com/embed/${ytId(current.link_url)}?autoplay=1&rel=0`}
                    allow="autoplay; encrypted-media; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>
              ) : (
                <div className="p-10 text-center text-white/70">
                  <FaYoutube size={40} className="mx-auto mb-2" />
                  This link can't be played here.
                  <a href={current.link_url} target="_blank" rel="noreferrer" className="block mt-2 text-red-400 underline">
                    Open it on YouTube
                  </a>
                </div>
              )}
            </div>

            <div className="flex flex-col min-h-0">
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                <span className="text-xs text-white/60 tabular-nums">
                  {player + 1} of {filtered.length}
                </span>
                <div className="flex gap-1">
                  <button type="button" onClick={() => setPlayer((i) => Math.max(0, i - 1))} disabled={player <= 0} className="yt-press w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center disabled:opacity-30" aria-label="Previous video">
                    <FaChevronLeft size={11} />
                  </button>
                  <button type="button" onClick={() => setPlayer((i) => Math.min(filtered.length - 1, i + 1))} disabled={player >= filtered.length - 1} className="yt-press w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center disabled:opacity-30" aria-label="Next video">
                    <FaChevronRight size={11} />
                  </button>
                  <button type="button" onClick={() => setPlayer(null)} className="yt-press w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center" aria-label="Close">
                    <FaTimes size={12} />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                <div className="flex items-center gap-3">
                  <Avatar src={current.user_profile} name={current.user_name} size={44} />
                  <div className="min-w-0">
                    <p className={`font-semibold truncate ${deletedUser(current) ? "text-rose-400 italic" : ""}`}>{current.user_name || "User Deleted"}</p>
                    <button type="button" onClick={() => copyText(current.user_id, "User ID copied")} className="text-xs text-white/60 hover:text-white tabular-nums inline-flex items-center gap-1">
                      {current.user_id} <FaCopy size={9} />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  {[
                    { icon: FaEye, label: "Views", value: fmt(current.views_num) },
                    { icon: FaMagic, label: "Karma used", value: fmt(current.point_used) },
                    { icon: FaClock, label: "Watch time", value: `${Number(current.watch_time || 0)}s` },
                    { icon: isShort(current) ? FaMobileAlt : FaFilm, label: "Type", value: current.link_type },
                  ].map((s) => {
                    const Icon = s.icon;
                    return (
                      <div key={s.label} className="rounded-xl bg-white/[.06] p-3">
                        <p className="flex items-center gap-1.5 text-[11px] text-white/50">
                          <Icon size={10} /> {s.label}
                        </p>
                        <p className="text-sm font-semibold mt-0.5">{s.value}</p>
                      </div>
                    );
                  })}
                </div>

                {mismatch(current) && (
                  <p className="flex items-start gap-2 rounded-xl bg-amber-400/15 text-amber-200 text-xs p-3">
                    <FaExclamationTriangle className="mt-0.5 shrink-0" /> This is a Short link but it was saved as a normal Video.
                  </p>
                )}

                <div className="rounded-xl bg-white/[.06] p-3 text-xs space-y-1.5">
                  <p className="flex justify-between gap-2">
                    <span className="text-white/50">Video Id</span>
                    <span className="tabular-nums">{current.post_id}</span>
                  </p>
                  <p className="flex justify-between gap-2">
                    <span className="text-white/50">Uploaded</span>
                    <span>
                      {fmtDate(parseAny(current.link_date))}, {fmtTime(parseAny(current.link_date))}
                    </span>
                  </p>
                  <p className="text-white/50 pt-1">Video Link</p>
                  <button type="button" onClick={() => copyText(current.link_url, "Link copied")} className="w-full text-left break-all text-white/90 hover:text-white">
                    {current.link_url} <FaCopy size={9} className="inline ml-1" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 p-4 border-t border-white/10">
                <a href={current.link_url} target="_blank" rel="noreferrer" className="yt-press h-10 rounded-xl bg-white/10 hover:bg-white/20 text-xs font-semibold inline-flex items-center justify-center gap-1.5">
                  <FaYoutube /> YouTube
                </a>
                <button type="button" onClick={() => navigate(`/user-profile/${current.user_id}`)} disabled={deletedUser(current)} className="yt-press h-10 rounded-xl bg-white/10 hover:bg-white/20 text-xs font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-30">
                  <FaUser size={10} /> Profile
                </button>
                <button type="button" onClick={() => setConfirm([current])} className="yt-press h-10 rounded-xl bg-red-600 hover:bg-red-500 text-xs font-semibold inline-flex items-center justify-center gap-1.5">
                  <FaTrash size={10} /> Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= Confirm ================= */}
      {confirm && confirm.length > 0 && (
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 1200 }}>
          <div className="yt-fade absolute inset-0 bg-slate-900/60" onClick={() => !working && setConfirm(null)} />
          <div className="yt-zoom relative w-full max-w-md rounded-2xl bg-white dark:bg-secondary-dark-bg shadow-2xl p-5">
            <span className="w-12 h-12 rounded-2xl bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300 flex items-center justify-center">
              <FaTrash size={18} />
            </span>
            <h3 className="mt-3 text-lg font-semibold text-slate-900 dark:text-white">{confirm.length === 1 ? "Delete this video?" : `Delete ${confirm.length} videos?`}</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">It will be removed from the app for everyone. Karma already spent on it is not returned. This cannot be undone.</p>
            <div className="mt-4 max-h-48 overflow-y-auto space-y-2">
              {confirm.slice(0, 6).map((v) => (
                <div key={v.post_id} className="flex items-center gap-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 p-2">
                  <div className="w-24 shrink-0 rounded-lg overflow-hidden">
                    {Thumb({ v })}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{v.user_name || "User Deleted"}</p>
                    <p className="text-xs text-slate-500">
                      {fmt(v.views_num)} views • {fmt(v.point_used)} karma
                    </p>
                  </div>
                </div>
              ))}
              {confirm.length > 6 && <p className="text-xs text-slate-500">and {confirm.length - 6} more</p>}
            </div>
            <div className="flex gap-2 mt-5">
              <button type="button" onClick={() => setConfirm(null)} disabled={working} className="yt-press flex-1 h-11 rounded-xl border border-slate-200 dark:border-slate-600 text-sm font-medium text-slate-700 dark:text-slate-200">
                Cancel
              </button>
              <button type="button" onClick={runDelete} disabled={working} className="yt-press flex-1 h-11 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
                {working ? <FaSyncAlt className="animate-spin" size={12} /> : <FaTrash size={12} />}
                {working ? "Deleting..." : "Yes, delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default YouTube;